package com.rajen.angrejidictionary.presentation.viewmodel

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import com.rajen.angrejidictionary.analytics.Umami
import com.rajen.angrejidictionary.data.local.AdminAuthManager
import com.rajen.angrejidictionary.data.remote.AdminApi
import com.rajen.angrejidictionary.data.remote.WordApi
import com.rajen.angrejidictionary.data.remote.dto.BroadcastNotificationRequest
import com.rajen.angrejidictionary.data.remote.dto.BroadcastNotificationResult
import com.rajen.angrejidictionary.data.remote.dto.ItemDto
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.launch

/**
 * Drives the "Send Broadcast Notification" form (POST /api/v1/admin/notifications/broadcast).
 * This fans a push out to every registered device token — Android + iOS only, web is
 * not covered by this endpoint. Optionally attaches a word (via `word_slug`) so the
 * notification deep-links straight to that word's detail screen.
 */
class NotificationsViewModel {

    private val scope = CoroutineScope(Dispatchers.Default)

    var title by mutableStateOf("")
        private set
    var description by mutableStateOf("")
        private set
    var imageUrl by mutableStateOf("")
        private set

    // ── Word picker ──────────────────────────────────────────────
    // Uses the public /items/search endpoint (no auth needed), same one the
    // admin word-search tab already relies on.

    var wordQuery by mutableStateOf("")
        private set
    var isSearchingWord by mutableStateOf(false)
        private set
    // null = no search run yet; empty list = search ran, no matches.
    var wordSearchResults by mutableStateOf<List<ItemDto>?>(null)
        private set
    var wordSearchError by mutableStateOf<String?>(null)
        private set
    var selectedWord by mutableStateOf<ItemDto?>(null)
        private set

    var isSending by mutableStateOf(false)
        private set
    var hasAttemptedSend by mutableStateOf(false)
        private set
    var sendError by mutableStateOf<String?>(null)
        private set
    var lastResult by mutableStateOf<BroadcastNotificationResult?>(null)
        private set
    var forceLogout by mutableStateOf(false)
        private set

    val isTitleValid: Boolean get() = title.isNotBlank()
    val isImageUrlValid: Boolean get() = imageUrl.isBlank() || imageUrl.trim().startsWith("https://")
    val isFormValid: Boolean get() = isTitleValid && isImageUrlValid && !isSending

    fun onTitleChange(value: String) {
        title = value
        sendError = null
    }

    fun onDescriptionChange(value: String) {
        description = value
        sendError = null
    }

    fun onImageUrlChange(value: String) {
        imageUrl = value
        sendError = null
    }

    fun onWordQueryChange(value: String) {
        wordQuery = value
        if (value.isBlank()) {
            wordSearchResults = null
            wordSearchError = null
        }
    }

    fun searchWord() {
        val term = wordQuery.trim()
        if (term.isBlank()) {
            wordSearchResults = null
            wordSearchError = null
            return
        }

        isSearchingWord = true
        wordSearchError = null

        scope.launch {
            try {
                val response = WordApi.searchWords(term)
                if (response.success && response.data != null) {
                    wordSearchResults = response.data
                    if (response.data.isEmpty()) {
                        wordSearchError = "No matching words found."
                    }
                } else {
                    wordSearchResults = emptyList()
                    wordSearchError = response.error?.message ?: "Search failed"
                }
            } catch (e: Exception) {
                wordSearchResults = emptyList()
                wordSearchError = "Network error: ${e.message}"
            } finally {
                isSearchingWord = false
            }
        }
    }

    /**
     * Picking a word suggests a title ("New word: X") when the admin hasn't
     * already typed one — never overwrites something they've written. Still
     * fully editable either way; this is just a starting point.
     */
    fun selectWord(item: ItemDto) {
        selectedWord = item
        wordQuery = ""
        wordSearchResults = null
        wordSearchError = null
        sendError = null
        if (title.isBlank()) {
            title = "New word: ${item.word.englishWord}"
        }
        Umami.trackAdminBroadcastWordSelect(item.word.englishWord)
    }

    fun clearSelectedWord() {
        selectedWord = null
    }

    fun send() {
        hasAttemptedSend = true
        if (!isFormValid) return

        isSending = true
        sendError = null
        lastResult = null

        val slug = selectedWord?.word?.englishWord?.trim()?.lowercase()

        val request = BroadcastNotificationRequest(
            title = title.trim(),
            description = description.trim().ifBlank { null },
            imageUrl = imageUrl.trim().ifBlank { null },
            wordSlug = slug
        )

        Umami.trackAdminBroadcastAttempt(
            hasDescription = request.description != null,
            hasImage = request.imageUrl != null,
            hasWord = request.wordSlug != null
        )

        scope.launch {
            try {
                val response = AdminApi.sendBroadcastNotification(request)
                if (response.success && response.data != null) {
                    lastResult = response.data
                    Umami.trackAdminBroadcastSuccess(
                        response.data.successCount,
                        response.data.failureCount,
                        response.data.prunedTokens
                    )
                    // Reset the form for the next broadcast, but leave the result
                    // card up so the admin can see confirmation it went out.
                    title = ""
                    description = ""
                    imageUrl = ""
                    selectedWord = null
                    hasAttemptedSend = false
                } else {
                    sendError = when (response.error?.type) {
                        "NOT_FOUND" -> "That word couldn't be found — please pick one from the search results."
                        else -> response.error?.message ?: "Failed to send broadcast notification"
                    }
                    Umami.trackAdminBroadcastError(response.error?.type ?: "unknown")
                }
            } catch (e: Exception) {
                val msg = e.message ?: ""
                if (msg.contains("401") || msg.contains("Unauthorized")) {
                    AdminAuthManager.logout()
                    forceLogout = true
                } else {
                    sendError = "Network error: $msg"
                    Umami.trackAdminBroadcastError("network-error")
                }
            } finally {
                isSending = false
            }
        }
    }

    fun clearResult() {
        lastResult = null
    }
}